import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bloggerService } from '../services/blogger';
import { cn } from '../lib/utils';
import { CategoriesSkeleton } from './Loaders';
import { useGlobals } from '../lib/useGlobals';

export function GlobalCategoryMenu() {
  const { globals } = useGlobals();
  const { data: categories, isLoading } = useSWR('categories', () => bloggerService.getCategories());
  const location = useLocation();
  const navigate = useNavigate();
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Check if custom nav menu exists
  let customNavData: any[] | null = null;
  try {
     if (globals.headerNavMenu) {
        customNavData = JSON.parse(globals.headerNavMenu);
        if (!Array.isArray(customNavData)) customNavData = null;
     }
  } catch(e) {}

  const allCategories = ['সব', ...(categories || [])];

  // Determine active category from URL
  let activeCategory = '';
  if (location.pathname === '/') {
    activeCategory = 'সব';
  } else if (location.pathname.startsWith('/category/')) {
    activeCategory = decodeURIComponent(location.pathname.split('/')[2]);
  }

  const checkScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScrollState();
    window.addEventListener('resize', checkScrollState);
    return () => window.removeEventListener('resize', checkScrollState);
  }, [categories, customNavData]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleSelect = (cat: string) => {
    if (cat === 'সব') {
      navigate('/');
    } else {
      navigate(`/category/${encodeURIComponent(cat)}`);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-950 border-b border-gray-100 dark:border-gray-800 py-2 px-4 shadow-sm print:hidden">
        <CategoriesSkeleton />
      </div>
    );
  }

  return (
    <div className="relative border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 py-2 group shadow-[0_2px_10px_-3px_rgba(0,0,0,0.05)] z-40 print:hidden overflow-hidden">
      {/* Left Arrow Button */}
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-gradient-to-r from-white dark:from-gray-950 via-white/80 dark:via-gray-950/80 to-transparent text-gray-700 dark:text-gray-300 transition-opacity"
          aria-label="Scroll left"
        >
          <div className="w-6 h-6 rounded-full bg-white dark:bg-gray-800 shadow-md flex items-center justify-center">
            <ChevronLeft size={16} />
          </div>
        </button>
      )}

      {/* Tabs Container */}
      <div 
        ref={scrollContainerRef}
        onScroll={checkScrollState}
        className="flex gap-2 overflow-x-auto no-scrollbar px-4 scroll-smooth"
      >
        {customNavData ? (
          customNavData.map((nav, idx) => (
             <Link
               key={idx}
               to={nav.url || '#'}
               className={cn(
                 "px-5 py-1.5 rounded-full text-sm font-bold whitespace-nowrap shrink-0 transition-all duration-200 font-bengali active:scale-95",
                 (location.pathname === nav.url)
                   ? "bg-[#D32F2F] text-[#FFFFFF] shadow-md"
                   : "bg-[#F1F1F1] dark:bg-gray-800 text-[#333333] dark:text-gray-300 border border-transparent dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
               )}
             >
               {nav.label}
             </Link>
          ))
        ) : (
          allCategories.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => handleSelect(cat)}
                className={cn(
                  "px-5 py-1.5 rounded-full text-sm font-bold whitespace-nowrap shrink-0 transition-all duration-200 font-bengali active:scale-95",
                  isActive
                    ? "bg-[#D32F2F] text-[#FFFFFF] shadow-md"
                    : "bg-[#F1F1F1] dark:bg-gray-800 text-[#333333] dark:text-gray-300 border border-transparent dark:border-gray-700"
                )}
              >
                {cat === 'সব' ? 'হোম' : cat}
              </button>
            );
          })
        )}
      </div>

      {/* Right Arrow Button */}
      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center bg-gradient-to-l from-white dark:from-gray-950 via-white/80 dark:via-gray-950/80 to-transparent text-gray-700 dark:text-gray-300 transition-opacity"
          aria-label="Scroll right"
        >
          <div className="w-6 h-6 rounded-full bg-white dark:bg-gray-800 shadow-md flex items-center justify-center">
            <ChevronRight size={16} />
          </div>
        </button>
      )}
    </div>
  );
}
